import React, { useContext, useEffect } from 'react';
import { Container, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import './_deliveryDockets.scss';
import sortBy from 'lodash/sortBy';
import { Download48, Info32 } from '@bphxd/ds-core-react/icons';
import FileIcon from '../../icons/FileIcon';
import { DeliveryContext, useAppContext } from '../../state/GlobalState';
import { getDeliveryValue } from '../../helpers/shipmentEngine';
import { newDocketClickHandler, roDocketClickHandler } from '../../analytics/events';
import { statuses, statusesSortRank } from '../../helpers/shipmentStatus';
import { getShipmentHeader } from '../../helpers/openOrders';
import pageTitleConstant from '../../analytics/constantPageTitle';
import { getDocketPdfUrl } from '../../api/graphQl/DocketPdf';
import LinkTileDeliveryDocket from '../LinkTileDeliveryDocket/LinkTileDeliveryDocket';
import VirtualTerminalModal from '../VirtualTerminalModal/VirtualTerminalModal';

const DeliveryDockets = ({ shipmentDetails, graphClient }) => {
  const history = useHistory();
  const location = useLocation();
  const [, setDeliveryId] = useContext(DeliveryContext);
  const { carrierId, shipmentId, handleGraphApiError, virtualTerminal, } = useAppContext();
  const [pdfUrls, setPdfUrls] = React.useState([]);
  const [virtualTerminalModalOpen, setVirtualTerminalModalOpen] = React.useState(false);
  // change display mode based on screen width
  const isMobile = window.screen.width < 768;

  // In case with multiple LIDs (Scen. 3) we're getting multiple shipments for different LIDs
  // So we have to order by the "higher" status of shipment with the same DeliveryNo
  const shipmentDetailsSortedByStatus = sortBy(
    shipmentDetails,
    (element) => statusesSortRank[element.Status],
  );

  const uniqueDocketsMappedByDeliveryNo = new Map();
  shipmentDetailsSortedByStatus.forEach((docket) => {
    uniqueDocketsMappedByDeliveryNo.set(getDeliveryValue(docket), docket);
  });

  const uniqueDocketNumbers = sortBy(
    Array.from(uniqueDocketsMappedByDeliveryNo.values()),
    (element) => getDeliveryValue(element),
  );

  const parseDocketKeyFromFilename = (filename = '') => {
    // Filename formats:
    // - ShipmentNo_307430117_CARRIERID_6910034_DeliveryNo_REDIRECT_8145449012-R2_LID_363012.pdf (with LID)
    // - ShipmentNo_307430117_CARRIERID_6910034_DeliveryNo_REDIRECT_8145449012-R1.pdf (without LID)
    const match = filename.match(/DeliveryNo_(REDIRECT|ORIGINAL)_([^_]+?)(?:_LID_|\.)/);
    if (!match) {
      return '';
    }
    const [, docketType, deliveryValue] = match;
    // Return key without LID to match against SK prefix
    return `DeliveryNo#${docketType}#${deliveryValue}`;
  };

  const pdfUrlByDeliveryKey = React.useMemo(() => {
    const map = new Map();
    pdfUrls.forEach((item) => {
      const deliveryKey = parseDocketKeyFromFilename(item?.filename);

      if (deliveryKey && item?.url) {
        map.set(deliveryKey, { url: item.url, filename: item.filename });
      }
    });
    return map;
  }, [pdfUrls]);

  const getPdfUrlForDocket = (docketSk) => {
    // SK format: DeliveryNo#REDIRECT#121212-R1#LID#363311
    // Extract DeliveryNo#TYPE#VALUE portion to match against filename
    const match = docketSk.match(/(DeliveryNo#[^#]+#[^#]+)#LID#/);
    if (!match) {
      return null;
    }
    const deliveryKey = match[1];
    return pdfUrlByDeliveryKey.get(deliveryKey);
  };

  const goToDeliveryDockets = (deliveryNo, status) => {
    setDeliveryId(deliveryNo);
    history.push('/delivery-docket');

    if (status === statuses.Loaded) {
      newDocketClickHandler(pageTitleConstant.shipmentDetailsPage);
    } else if (status === statuses.Delivered
      || status === statuses.PDFGenerated
      || status === statuses.EmailSent
    ) {
      roDocketClickHandler(pageTitleConstant.shipmentDetailsPage);
    }
  };

  const handleDownloadPdf = (pdfUrl, filename) => {
    if (pdfUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename || 'delivery-docket.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getPdfUrls = async (shipmentId, carrierId) => {
    try {
      const response = await getDocketPdfUrl(graphClient, shipmentId, carrierId);
      if (response?.data?.generateeDocketResponse?.presigned_urls) {
        setPdfUrls(response?.data?.generateeDocketResponse?.presigned_urls);
      }
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  const closeVirtualTerminalModal = () => {
    setVirtualTerminalModalOpen(false);
  }

  useEffect(() => {
    getPdfUrls(shipmentId, carrierId);
  }, [shipmentId, carrierId]);

  useEffect(() => {
    const fromFindShipment = Boolean(location?.state?.fromFindShipment);
    setVirtualTerminalModalOpen(Boolean(virtualTerminal && fromFindShipment));
  }, [virtualTerminal, location.state]);

  return (
    <Container className="delivery-dockets-container p-0">
      {
        uniqueDocketNumbers.map((deliveryDocket) => {
          const pdfData = getPdfUrlForDocket(deliveryDocket.SK);
          const pdfUrl = pdfData?.url;
          const filename = pdfData?.filename;
          return (
            <div role="button" key={deliveryDocket.SK} style={{ display: 'flex', gap: '8px' }}>
              <LinkTileDeliveryDocket
                icon={FileIcon}
                title="Delivery Docket"
                line1={deliveryDocket?.ShipmentAddress?.ShipToName}
                line2={getShipmentHeader(getDeliveryValue(deliveryDocket))}
                key={deliveryDocket.SK}
                docketStatus={deliveryDocket.Status}
                onClick={() => goToDeliveryDockets(getDeliveryValue(deliveryDocket),
                  deliveryDocket.Status)}
              />
              {!pdfUrl && (
                <button
                  disabled={true}
                  className={`d-flex justify-content-between align-items-center rounded my-5 p-3 link-tile-container`}
                >
                  <Download48 />
                </button>
              )}
              {pdfUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadPdf(pdfUrl, filename);
                  }}
                  className={`d-flex justify-content-between align-items-center rounded my-5 p-3 link-tile-container`}
                  title="Download Docket"
                >
                  <Download48 />
                </button>
              )}
            </div>
          );
        })
      }
      <div className="mb-2 d-flex justify-content-center">
        <Info32 className={isMobile ? 'info-icon mt-n1 ml-7 mr-1' : 'info-icon mt-n3 ml-1'} />
        <Label className="validation-info arial font-weight-normal font-12 text-left mr-6">
          If download icon is disabled, it means the delivery docket is not available for download yet.
          Please refresh the page / navigate back after some time or contact administrator ({<a href={`mailto:${process.env.REACT_APP_CONTACT_ADMIN_EMAIL}`}>{process.env.REACT_APP_CONTACT_ADMIN_EMAIL}</a>}) if you need further assistance.
        </Label>
      </div>
      
      <VirtualTerminalModal isOpen={virtualTerminalModalOpen} handleCancel={closeVirtualTerminalModal} />
    </Container>
  );
};

DeliveryDockets.propTypes = {
  shipmentDetails: PropTypes.arrayOf(PropTypes.shape({
    SK: PropTypes.string.isRequired,
  })).isRequired,
  graphClient: PropTypes.object.isRequired,
};

export default DeliveryDockets;
